<script setup>
import CloseButton from "@/Components/UI/buttons/CloseButton.vue";
import cn from "@/helpers/cn.js";

// The white modal panel: chrome, header row, content padding. Shared by BaseModal and
// ConfirmActionDialog — the latter is service-owned and must outlive its trigger, so it
// cannot wrap BaseModal; sharing this panel is what keeps the two identical.
const props = defineProps({
    title: { type: String, default: null },
    // Confirm dialogs center the title in the body under the artwork, leaving the header
    // row with only the close ×. Everything else stays identical to a titled modal.
    titleInBody: { type: Boolean, default: false },
    closeable: { type: Boolean, default: true },
    class: { type: String, default: "" },
});

defineEmits(["close"]);
</script>

<template>
    <div
        :class="cn('bg-white rounded-md md:rounded-lg relative shadow-dropdown w-full modal focus:outline-none', props.class)">
        <!-- The × sits in a 40px (mobile) / 44px (desktop) hit target, so its glyph is
             inset from the button box: -mr pulls the glyph onto the content edge, -my keeps
             the box from inflating the row. Both are tuned per breakpoint — changing the
             button size or the panel padding needs a re-measure, not a guess. -->
        <div
            v-if="!titleInBody"
            class="flex items-center justify-between gap-4 px-7 md:px-10 pt-10 pb-4">
            <h3 class="flex items-center gap-1 text-xl font-semibold min-w-0">
                <slot name="title">{{ title }}</slot>
            </h3>
            <CloseButton
                v-if="closeable"
                data-testid="modal-close"
                class="-my-4 -mr-7 md:-mr-8 shrink-0 text-2xl"
                title="Закрыть"
                @click="$emit('close')" />
        </div>

        <!-- Floated so it costs no vertical space and the artwork can sit near the top. -->
        <CloseButton
            v-else-if="closeable"
            data-testid="modal-close"
            class="absolute top-6 right-8 md:right-10 -mr-8 z-10 text-2xl"
            title="Закрыть"
            @click="$emit('close')" />

        <!-- Banners / KB sub-header, below the title row. -->
        <slot name="header" />

        <div :class="cn('px-7 md:px-10 relative pb-10', titleInBody && 'pt-10')">
            <slot />
        </div>

        <slot name="footer" />
    </div>
</template>
